import React from "react";
import { backend } from "Backend";
import { Box, IconButton, LinearProgress, Paper, styled, Tooltip, Typography } from "@mui/material";

import { Annotation } from "components/PDFCanvas";
import BadReadCard from "./BadReadCard";
import { Checkmark } from "components/Icons";
import HitsBrowserLayout, { NavButton } from "./HitsBrowserLayout";

import { useAppSelector } from "redux/hooks";
import { Block, DocumentIntake } from "generated/graphql";
import IntakeViewer from "components/DocumentViewer/IntakeViewer";
import { useNavigate } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import theme from "theme";
import { selectIntakeByDocumentSegmentId } from "redux/slices/case";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";

const HitCardBox = styled(Box)({
  padding: theme.spacing(1, 0),
  display: "flex",
});

const BadReadsEmptyPaper = styled(Paper)({
  display: "flex",
  height: 200,
  width: "100%",
  padding: theme.spacing(1),
  alignItems: "center",
  justifyContent: "center",
});

const PAGE_SIZE = 10;

export interface BadReadsBrowserProps {
  lensId: number;
  caseId: number;
}

const BadReadsBrowser = (props: BadReadsBrowserProps) => {
  const { searchParams, updateParam } = useSearchParamsWithUpdate();
  const { lensId, caseId } = props;
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(searchParams.toString());
  const hitNum = parseInt(queryParams.get("hit") || "") - 1 || 0;

  const setHitNum = (n: number) => {
    updateParam("hit", `${n + 1}`);
  };

  const [badBlocks, setBadBlocks] = React.useState<Block[][]>([]);
  const [fullCount, setFullCount] = React.useState(0);

  const badReadGroup: Block[] | undefined = badBlocks[hitNum] as Block[] | undefined;
  const badReadFirstBlock: Block | undefined = badReadGroup && badReadGroup[0];

  const page = badReadFirstBlock?.page;
  const documentId = badReadFirstBlock?.documentId;

  const intake: DocumentIntake | undefined = useAppSelector(selectIntakeByDocumentSegmentId(documentId));

  const needToFetch = hitNum + 1 >= badBlocks.length && badBlocks.length < fullCount;

  const [pageNum, setPageNum] = React.useState<number>(page || 0);
  React.useEffect(() => {
    if (page) setPageNum(page);
  }, [page]);

  const hasNextElem = hitNum + 1 < fullCount;
  const hasPrevElem = Boolean(badBlocks[hitNum - 1]);

  const [loading, setLoading] = React.useState(false);

  const offPage = pageNum !== page;
  const shouldAnnotate = !offPage;

  const badReadAnnotations: Annotation[] = badReadGroup
    ? badReadGroup.map((b) => ({ ...b.boundingBox, color: "red" }))
    : [];

  const canvasAnnotations: Annotation[] = shouldAnnotate ? badReadAnnotations : [];

  React.useEffect(() => {
    setHitNum(0);
  }, [fullCount]);

  React.useEffect(() => {
    setLoading(true);
    backend.getCaseBadBlocks(caseId, 0, PAGE_SIZE).then((response) => {
      backend.mapSuccess(response, (response) => {
        setBadBlocks(response.blockGroups);
        setFullCount(response.fullCount);
      });
      setLoading(false);
    });
  }, [lensId, caseId]);

  React.useEffect(() => {
    if (badReadGroup) {
      backend.getCaseBadBlocks(caseId, badBlocks.length, PAGE_SIZE).then((response) => {
        backend.mapSuccess(response, (response) => {
          setBadBlocks((old) => old.concat(response.blockGroups));
          setFullCount(response.fullCount);
        });
      });
    }
  }, [needToFetch]);

  const onDocumentLinkClick = () => {
    if (intake) {
      const url = backend.getIntakeDownloadUrl(intake.id);
      window.open(url, "_blank");
    }
  };

  const dismissPage = () => {
    if (!documentId) return;
    backend.mapSuccessAsync(backend.editDocumentBlocksQA(documentId, true, page), (_response) => {
      backend.mapSuccessAsync(backend.getCaseBadBlocks(caseId, 0, 0), (response) => {
        setBadBlocks((old) => old.filter((blocks) => blocks[0]?.page !== page));
        setFullCount(response.fullCount);
      });
    });
  };

  const banner = (
    <>
      <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
        <Tooltip title="Back to Case" arrow>
          <IconButton size="small" color="default" sx={{ mr: 2 }} onClick={() => navigate("../")}>
            <ArrowBack />
          </IconButton>
        </Tooltip>
      </Box>
      <Box display="flex" justifyContent="space-between">
        {fullCount > 0 && (
          <div>
            <Typography style={{ margin: "auto 0" }}>
              Reviewing item {hitNum + 1} of {fullCount}
            </Typography>
            <LinearProgress color="secondary" variant="determinate" value={((hitNum + 1) / fullCount) * 100} />
          </div>
        )}
      </Box>
    </>
  );

  const control = (
    <>
      {badReadGroup ? (
        <HitCardBox>
          {/* BadReadCard maintains local state; we use key to wipe that state when badReadGroup changes */}
          <BadReadCard
            lensId={lensId}
            caseId={caseId}
            intake={intake}
            key={badReadFirstBlock?.index}
            blocks={badReadGroup}
            onDocumentClick={onDocumentLinkClick}
            dismissPage={dismissPage}
          />
        </HitCardBox>
      ) : loading ? (
        <HitCardBox>
          <BadReadsEmptyPaper>
            <Typography variant="subtitle1">Loading...</Typography>
          </BadReadsEmptyPaper>
        </HitCardBox>
      ) : (
        <HitCardBox>
          <BadReadsEmptyPaper>
            <Checkmark />
            <Typography variant="subtitle1">No low quality documents to review</Typography>
          </BadReadsEmptyPaper>
        </HitCardBox>
      )}
      {fullCount > 0 && (
        <Box display="flex">
          <NavButton dir="previous" disabled={!hasPrevElem} onClick={() => setHitNum(hitNum - 1)} />
          <NavButton dir="next" disabled={!hasNextElem || needToFetch} onClick={() => setHitNum(hitNum + 1)} />
        </Box>
      )}
    </>
  );

  const document = intake ? (
    <IntakeViewer
      lensId={lensId}
      caseId={caseId}
      additionalAnnotations={canvasAnnotations}
      intake={intake}
      pageNumberHandle={[pageNum, setPageNum]}
    />
  ) : (
    <div />
  );

  return <HitsBrowserLayout banner={banner} control={control} document={document} hideDoc={false} />;
};

export default BadReadsBrowser;
